import { useMemo, useRef, useEffect, type ComponentProps } from "react"
import { ChevronRight } from "lucide-react"
import cn from "@/v2-ui/utils/utils.cn"
import Link from "@/v2-backoffice-client/components/link"
import { useAppViewSheetOpener } from "@/v2-backoffice-client/app/app.utils"
import type { AppViewSheetKind } from "@/v2-backoffice-client/app/AppRouter"

const KIND_LABELS: Record<string, string> = {
  customer: "Customer",
  tenant: "Team",
  user: "User",
  invitation: "Invitation",
  plan: "License",
  billingAccount: "Billing Account",
  featureGroup: "Package",
  addOn: "Add-on",
  capability: "Capability",
  mapContent: "Map content",
  layer: "Layer",
  layerCategory: "Layer Category",
}

type ViewSheetCrumb = {
  label: string
  to?: string
}

export type VStackEntry = {
  kind: AppViewSheetKind
  id: string
}

function buildCrumbs(stack: VStackEntry[]): ViewSheetCrumb[] {
  return stack.map((entry, i) => {
    const isLast = i === stack.length - 1
    const kindLabel = KIND_LABELS[entry.kind as string] ?? entry.kind
    const label = `${kindLabel}: ${entry.id}`
    const truncatedTo = `?v=${entry.kind}:${entry.id}`

    return { label, to: isLast ? undefined : truncatedTo }
  })
}

export type AppViewSheetBreadcrumbProps = ComponentProps<"nav">

function AppViewSheetBreadcrumb(props: AppViewSheetBreadcrumbProps) {
  const { className, ...navProps } = props
  const { stack } = useAppViewSheetOpener()

  const crumbs = useMemo(() => buildCrumbs(stack), [stack])

  const ref = useRef<HTMLElement>(null)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const obs = new ResizeObserver(() => {
      el.scrollLeft = el.scrollWidth - el.clientWidth
    })
    obs.observe(el)
    return () => obs.disconnect()
  }, [])

  if (crumbs.length === 0) return null

  return (
    <nav className={cn("breadcrumb", className)} ref={ref} {...navProps}>
      {crumbs.map((crumb, index) => (
        <span key={index} className="breadcrumb-item">
          {crumb.to ? (
            <Link
              className="breadcrumb-badge breadcrumb-link"
              iconSrc={null}
              to={crumb.to}
            >
              {crumb.label}
            </Link>
          ) : (
            <span className="breadcrumb-badge breadcrumb-current">
              {crumb.label}
            </span>
          )}
          <ChevronRight className="breadcrumb-separator" />
        </span>
      ))}
    </nav>
  )
}

export default AppViewSheetBreadcrumb
